import type { Category } from "@/components/DefaultImage"
import type { Block } from "@/templates/HomePage"
import classNames from "classnames"
import { graphql, useStaticQuery } from "gatsby"
import Button from "@/components/Button"
import DefaultImage from "@/components/DefaultImage"
import Rows from "@/components/Rows"
import { DateFormatter, formatDateURL } from "@/libs/helpers"
import * as styles from "@/styles/modules/StoryBlockCard.module.scss"

export default function StoryBlockCard({
  isFeatured,
  ...block
}: Block & { isFeatured?: boolean }) {
  const data: Queries.CategoriesQuery = useStaticQuery(graphql`
    query Categories {
      allSanityBlockCategories {
        nodes {
          name
          slug {
            current
          }
          theme {
            rgb {
              r
              g
              b
            }
          }
          group {
            slug {
              current
            }
          }
        }
      }
    }
  `)

  const blockCategory = data.allSanityBlockCategories.nodes.find(
    cat => cat.name === block.category?.name,
  )

  const categoryGroupSlug = blockCategory?.group?.slug.current
  const categorySlug = blockCategory?.slug.current

  const relativePath = `/${[categoryGroupSlug, categorySlug]
    .filter(Boolean)
    .join("/")}/${formatDateURL(block.date)}/${block.slug}`

  const D = new DateFormatter(block.date)

  const displayDate = `${D.MMMM} ${D.D}, ${D.YYYY}`

  const authors = block.author?.join(" & ")

  const image = block.elements.find(elem => elem?.type === "Image")?.content as
    | { url?: string; caption?: string }
    | undefined

  const heading = block.elements.find(elem => elem?.type === "Heading")
    ?.content as string | undefined

  return (
    <div
      className={classNames(styles.wrapper, { [styles.featured]: isFeatured })}
    >
      {image ? (
        <img
          className={styles.image}
          src={image.url}
          loading="lazy"
          alt="" // TODO: Add capability for alt text
        />
      ) : (
        <DefaultImage category={blockCategory?.name as Category} />
      )}

      <Rows gap={1}>
        <div className={styles.categoryDateWrapper}>
          {blockCategory && (
            <h6
              className={styles.category}
              style={{
                color: `rgb(${blockCategory.theme.rgb?.r}, ${blockCategory.theme.rgb?.g}, ${blockCategory.theme.rgb?.b})`,
              }}
            >
              {blockCategory.name}
            </h6>
          )}
          <span className={styles.date}>
            &nbsp;&nbsp;<span style={{ opacity: 0.4 }}>|</span>&nbsp;{" "}
            {displayDate}
          </span>
        </div>
        <h3>{heading}</h3>
        {block.tlDrExcerpt && (
          <div
            dangerouslySetInnerHTML={{ __html: block.tlDrExcerpt }}
            className={styles.tldr}
          />
        )}
        <div className={styles.foot}>
          {authors && <div className={styles.author}>{authors}</div>}
          <Button to={relativePath} uiType="secondary" className={styles.cta}>
            Read More
          </Button>
        </div>
      </Rows>
    </div>
  )
}
